import { useConfig } from '@exam-center/configuration';
import { type GetMyExamCentersQuery } from '@exam-center/graphql';
import { CheckIcon, ExpandMoreRoundedIcon, Flex, Menu, MenuItem, Rotate } from '@shared/components';
import { setStorageItem } from '@shared/utils';
import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import ExamCenterItem from './exam-center-item';
import ExamCenterSelectionItem from './exam-center-selection-item';

type ExamCenter = GetMyExamCentersQuery['ecGetMyExamCenters']['examCenters'][number];

interface ExamCenterSelectionProps {
  minify: boolean;
  currentExamCenter: ExamCenter;
  examCenters: ExamCenter[];
  loading: boolean;
}

export default function ExamCenterSelection(props: Readonly<ExamCenterSelectionProps>) {
  const { minify: shouldMinify, currentExamCenter, examCenters, loading: isLoading } = props;

  const navigate = useNavigate();

  const { examCenter } = useConfig();
  const { defaultLogoSrc } = examCenter;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isOpen = anchorEl !== null;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLLIElement>) => {
    const targetExamCenterCode = event.currentTarget.dataset.value ?? '';
    setStorageItem('examCenterCode', targetExamCenterCode);
    navigate(generatePath('/:examCenterCode', { examCenterCode: targetExamCenterCode }));
    handleClose();
  };

  if (isLoading) {
    return (
      <ExamCenterSelectionItem as='div'>
        <ExamCenterItem name='Loading' />
      </ExamCenterSelectionItem>
    );
  }

  if (examCenters.length === 0) {
    return null;
  }

  return (
    <Flex>
      <ExamCenterSelectionItem disabled={examCenters.length === 1} onClick={handleClick}>
        <ExamCenterItem
          logoSrc={currentExamCenter?.logoSrc ?? defaultLogoSrc}
          name={currentExamCenter?.nameTH ?? 'ไม่มี'}
          minify={shouldMinify}
        />
        {!shouldMinify && examCenters.length > 1 ? (
          <Rotate in={isOpen}>
            <ExpandMoreRoundedIcon sx={{ fontSize: '24px' }} />
          </Rotate>
        ) : null}
      </ExamCenterSelectionItem>
      <Menu
        sx={{
          '& .MuiMenu-list': {
            width: '208px',
          },
        }}
        id='exam-center-selection-menu'
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: (theme) => theme.spacing(1),
          }}
        >
          {examCenters.map((examCenter) => {
            const { id, code, nameTH, logoSrc } = examCenter;
            const isSelected = currentExamCenter?.code?.toLowerCase() === code.toLowerCase();

            return (
              <MenuItem
                data-value={code}
                key={id}
                onClick={handleMenuClick}
                selected={isSelected}
                sx={{ justifyContent: 'space-between' }}
              >
                <ExamCenterItem logoSrc={logoSrc ?? defaultLogoSrc} name={nameTH} />
                {isSelected ? (
                  <CheckIcon
                    sx={{ fontSize: '24px', color: (theme) => theme.palette.primary.main }}
                  />
                ) : null}
              </MenuItem>
            );
          })}
        </Flex>
      </Menu>
    </Flex>
  );
}
